import { DataOutputStream, FileSystem } from './java/DataStream';
import { CoinShop } from './CoinShop';
import { PlayerSkins } from './PlayerSkins';

/**
 * Persistent player profile — XP, level, lifetime statistics, run history.
 * Single file: dotch_profile.dat (versioned binary).
 * Absorbs and extends what Stats.java tracked.
 */
export class Profile {

  private static readonly FILE = 'dotch_profile.dat';
  private static readonly VERSION = 3;

  // ===== XP, Level & Coins =====
  private static totalXp = 0;
  private static level = 1;
  private static coins = 0;
  private static runCoinsEarned = 0;

  // ===== Per-difficulty stats (0=Normal, 1=Hard, 2=Insane) =====
  private static attempts: number[] = [0, 0, 0];
  private static highScores: number[] = [0, 0, 0];
  private static highLevels: number[] = [0, 0, 0];
  private static bestTimeTicks: number[] = [0, 0, 0]; // ticks survived (best)

  // ===== Lifetime aggregates =====
  private static totalGames = 0;
  private static totalDeaths = 0;
  private static totalTimeTicks = 0;   // total ticks across all runs
  private static totalScore = 0;       // cumulative score
  private static totalDamageTaken = 0;
  private static totalUpgradesBought = 0;
  private static totalHealthUps = 0;
  private static totalSpeedUps = 0;
  private static totalRefills = 0;
  private static longestStreakTicks = 0; // longest no-damage streak ever
  private static totalBossesDefeated = 0;

  // Enemy encounter counts (lifetime)
  private static enemyBasic = 0;
  private static enemyFast = 0;
  private static enemySmart = 0;
  private static enemyHard = 0;
  private static enemyBoss = 0;

  // Recent run history (last 20 scores) — circular buffer
  private static readonly HISTORY_SIZE = 20;
  private static recentScores: number[] = new Array<number>(Profile.HISTORY_SIZE).fill(0);
  private static recentLevels: number[] = new Array<number>(Profile.HISTORY_SIZE).fill(0);
  private static recentDifficulty: number[] = new Array<number>(Profile.HISTORY_SIZE).fill(0); // 0/1/2
  private static historyIndex = 0; // next write position
  private static historyCount = 0; // total entries (up to HISTORY_SIZE)

  // Current run tracking (not persisted, reset each run)
  private static currentAttempt = 0;
  private static runXpEarned = 0;
  private static runLeveledUp = false;
  private static runLevelBefore = 1;

  static init(): void {
    Profile.load();
  }

  // ==================== XP System ====================

  /**
   * XP formula per run:
   * base = score / 10
   * levelBonus = levelsReached * 50
   * diffMultiplier = Normal 1.0, Hard 1.5, Insane 2.0
   * bossBonus = bossesDefeated * 100
   * total = (base + levelBonus + bossBonus) * diffMultiplier
   */
  static calculateRunXp(score: number, levelsReached: number, difficulty: number, bossesDefeated: number): number {
    const diffMult = difficulty === 0 ? 1.0 : difficulty === 1 ? 1.5 : 2.0;
    const base = Math.trunc(score / 10);
    const levelBonus = levelsReached * 50;
    const bossBonus = bossesDefeated * 100;
    return Math.trunc((base + levelBonus + bossBonus) * diffMult);
  }

  /**
   * XP required to reach the next level.
   * Follows a soft exponential: 500 + level * 200 + level^1.5 * 50
   * Level 1→2: 780, Level 5→6: 2060, Level 10→11: 3680, Level 20→21: 6970
   */
  static xpForNextLevel(lvl: number): number {
    return Math.trunc(500 + lvl * 200 + Math.pow(lvl, 1.5) * 50);
  }

  /** XP accumulated within current level (for progress bar) */
  static xpInCurrentLevel(): number {
    let xp = Profile.totalXp;
    for (let i = 1; i < Profile.level; i++) {
      xp -= Profile.xpForNextLevel(i);
    }
    return Math.max(0, xp);
  }

  static levelProgress(): number {
    const needed = Profile.xpForNextLevel(Profile.level);
    if (needed <= 0) return 0;
    return Math.min(1, Profile.xpInCurrentLevel() / needed);
  }

  private static addXp(xp: number): void {
    Profile.totalXp += xp;
    while (Profile.xpInCurrentLevel() >= Profile.xpForNextLevel(Profile.level)) {
      Profile.level++;
      Profile.runLeveledUp = true;
    }
  }

  /**
   * Coins earned per run:
   * base = score / 50
   * levelBonus = levelsReached * 10
   * bossBonus = bossesDefeated * 25
   * diffMultiplier = Normal 1.0, Hard 1.3, Insane 1.6
   */
  static calculateRunCoins(score: number, levelsReached: number, difficulty: number, bossesDefeated: number): number {
    const diffMult = difficulty === 0 ? 1.0 : difficulty === 1 ? 1.3 : 1.6;
    const base = Math.trunc(score / 50);
    const levelBonus = levelsReached * 10;
    const bossBonus = bossesDefeated * 25;
    return Math.trunc((base + levelBonus + bossBonus) * diffMult);
  }

  // Coin accessors
  static getCoins(): number { return Profile.coins; }
  static getRunCoinsEarned(): number { return Profile.runCoinsEarned; }

  static spendCoins(amount: number | bigint): boolean {
    const amt = Number(amount);
    if (amt > Profile.coins) return false;
    Profile.coins -= amt;
    Profile.save();
    return true;
  }

  // ==================== Run lifecycle ====================

  /** Called when a new run starts */
  static startRun(difficulty: number): void {
    if (difficulty >= 0 && difficulty < 3) {
      Profile.attempts[difficulty]++;
      Profile.currentAttempt = Profile.attempts[difficulty];
    }
    Profile.totalGames++;
    Profile.runXpEarned = 0;
    Profile.runLeveledUp = false;
    Profile.runLevelBefore = Profile.level;
    Profile.save();
  }

  /**
   * Called when a run ends (player dies).
   * @return XP earned this run
   */
  static endRun(difficulty: number, score: number, levelReached: number, ticksSurvived: number,
                healthUps: number, speedUps: number, refills: number,
                longestStreak: number, bossesDefeated: number,
                damageTaken: number,
                encBasic: number, encFast: number, encSmart: number, encHard: number, encBoss: number): number {
    Profile.totalDeaths++;

    // Per-difficulty bests
    if (difficulty >= 0 && difficulty < 3) {
      if (score > Profile.highScores[difficulty]) Profile.highScores[difficulty] = score;
      if (levelReached > Profile.highLevels[difficulty]) Profile.highLevels[difficulty] = levelReached;
      if (ticksSurvived > Profile.bestTimeTicks[difficulty]) Profile.bestTimeTicks[difficulty] = ticksSurvived;
    }

    // Lifetime aggregates
    Profile.totalScore += score;
    Profile.totalTimeTicks += ticksSurvived;
    Profile.totalDamageTaken += Math.trunc(damageTaken);
    Profile.totalUpgradesBought += healthUps + speedUps + refills;
    Profile.totalHealthUps += healthUps;
    Profile.totalSpeedUps += speedUps;
    Profile.totalRefills += refills;
    Profile.totalBossesDefeated += bossesDefeated;
    if (longestStreak > Profile.longestStreakTicks) Profile.longestStreakTicks = longestStreak;

    // Enemy encounters
    Profile.enemyBasic += encBasic;
    Profile.enemyFast += encFast;
    Profile.enemySmart += encSmart;
    Profile.enemyHard += encHard;
    Profile.enemyBoss += encBoss;

    // Recent history
    Profile.recentScores[Profile.historyIndex] = score;
    Profile.recentLevels[Profile.historyIndex] = levelReached;
    Profile.recentDifficulty[Profile.historyIndex] = difficulty;
    Profile.historyIndex = (Profile.historyIndex + 1) % Profile.HISTORY_SIZE;
    if (Profile.historyCount < Profile.HISTORY_SIZE) Profile.historyCount++;

    // XP
    const xp = Profile.calculateRunXp(score, levelReached, difficulty, bossesDefeated);
    Profile.runXpEarned = xp;
    Profile.addXp(xp);

    // Coins (with Coin Magnet multiplier from shop)
    const earnedCoins = Math.trunc(Profile.calculateRunCoins(score, levelReached, difficulty, bossesDefeated)
      * CoinShop.getCoinMultiplier());
    Profile.runCoinsEarned = earnedCoins;
    Profile.coins += earnedCoins;

    Profile.save();
    return xp;
  }

  // ==================== Getters ====================

  // XP & Level
  static getTotalXp(): number { return Profile.totalXp; }
  static getLevel(): number { return Profile.level; }
  static getRunXpEarned(): number { return Profile.runXpEarned; }
  static didLevelUp(): boolean { return Profile.runLeveledUp; }
  static getLevelBefore(): number { return Profile.runLevelBefore; }

  // Per-difficulty
  static getAttempts(diff: number): number { return (diff >= 0 && diff < 3) ? Profile.attempts[diff] : 0; }
  static getHighScore(diff: number): number { return (diff >= 0 && diff < 3) ? Profile.highScores[diff] : 0; }
  static getHighLevel(diff: number): number { return (diff >= 0 && diff < 3) ? Profile.highLevels[diff] : 0; }
  static getBestTime(diff: number): string {
    if (diff < 0 || diff >= 3) return '0s';
    return Profile.formatTicks(Profile.bestTimeTicks[diff]);
  }
  static getCurrentAttempt(): number { return Profile.currentAttempt; }

  // Lifetime
  static getTotalGames(): number { return Profile.totalGames; }
  static getTotalDeaths(): number { return Profile.totalDeaths; }
  static getTotalTimePlayed(): string { return Profile.formatTicks(Math.trunc(Math.min(Profile.totalTimeTicks, 2147483647))); }
  static getTotalScore(): number { return Profile.totalScore; }
  static getTotalDamageTaken(): number { return Profile.totalDamageTaken; }
  static getTotalUpgrades(): number { return Profile.totalUpgradesBought; }
  static getTotalHealthUps(): number { return Profile.totalHealthUps; }
  static getTotalSpeedUps(): number { return Profile.totalSpeedUps; }
  static getTotalRefills(): number { return Profile.totalRefills; }
  static getLongestStreak(): number { return Math.trunc(Profile.longestStreakTicks / 60); } // in seconds
  static getTotalBossesDefeated(): number { return Profile.totalBossesDefeated; }

  // Enemy encounters
  static getEnemyBasic(): number { return Profile.enemyBasic; }
  static getEnemyFast(): number { return Profile.enemyFast; }
  static getEnemySmart(): number { return Profile.enemySmart; }
  static getEnemyHard(): number { return Profile.enemyHard; }
  static getEnemyBoss(): number { return Profile.enemyBoss; }

  // Derived
  static getAvgSurvivalSeconds(): number {
    if (Profile.totalGames === 0) return 0;
    return Math.trunc(Math.trunc(Profile.totalTimeTicks / Profile.totalGames) / 60);
  }

  static getFavoriteDifficulty(): number {
    let max = 0, fav = 0;
    for (let i = 0; i < 3; i++) {
      if (Profile.attempts[i] > max) { max = Profile.attempts[i]; fav = i; }
    }
    return fav;
  }

  static getDifficultyName(diff: number): string {
    return diff === 0 ? 'Normal' : diff === 1 ? 'Hard' : 'Insane';
  }

  // History
  static getHistoryCount(): number { return Profile.historyCount; }

  /** Get recent run at index (0 = most recent) */
  static getRecentScore(ago: number): number {
    if (ago >= Profile.historyCount) return 0;
    const idx = (Profile.historyIndex - 1 - ago + Profile.HISTORY_SIZE * 2) % Profile.HISTORY_SIZE;
    return Profile.recentScores[idx];
  }

  static getRecentLevel(ago: number): number {
    if (ago >= Profile.historyCount) return 0;
    const idx = (Profile.historyIndex - 1 - ago + Profile.HISTORY_SIZE * 2) % Profile.HISTORY_SIZE;
    return Profile.recentLevels[idx];
  }

  static getRecentDifficulty(ago: number): number {
    if (ago >= Profile.historyCount) return 0;
    const idx = (Profile.historyIndex - 1 - ago + Profile.HISTORY_SIZE * 2) % Profile.HISTORY_SIZE;
    return Profile.recentDifficulty[idx];
  }

  // ==================== Compat with Stats.java interface ====================

  /** Called by existing code that uses Stats.submitScore */
  static submitScore(difficulty: number, score: number): boolean {
    if (difficulty >= 0 && difficulty < 3 && score > Profile.highScores[difficulty]) {
      Profile.highScores[difficulty] = score;
      Profile.save();
      return true;
    }
    return false;
  }

  static resetHighScores(): void {
    Profile.highScores = [0, 0, 0];
    Profile.highLevels = [0, 0, 0];
    Profile.bestTimeTicks = [0, 0, 0];
    Profile.save();
  }

  static resetAll(): void {
    Profile.totalXp = 0; Profile.level = 1;
    Profile.attempts = [0, 0, 0]; Profile.highScores = [0, 0, 0]; Profile.highLevels = [0, 0, 0]; Profile.bestTimeTicks = [0, 0, 0];
    Profile.totalGames = 0; Profile.totalDeaths = 0; Profile.totalTimeTicks = 0; Profile.totalScore = 0; Profile.totalDamageTaken = 0;
    Profile.totalUpgradesBought = 0; Profile.totalHealthUps = 0; Profile.totalSpeedUps = 0; Profile.totalRefills = 0;
    Profile.longestStreakTicks = 0; Profile.totalBossesDefeated = 0;
    Profile.enemyBasic = 0; Profile.enemyFast = 0; Profile.enemySmart = 0; Profile.enemyHard = 0; Profile.enemyBoss = 0;
    Profile.recentScores = new Array<number>(Profile.HISTORY_SIZE).fill(0);
    Profile.recentLevels = new Array<number>(Profile.HISTORY_SIZE).fill(0);
    Profile.recentDifficulty = new Array<number>(Profile.HISTORY_SIZE).fill(0);
    Profile.historyIndex = 0; Profile.historyCount = 0;
    Profile.currentAttempt = 0;
    Profile.coins = 0;
    Profile.save();
  }

  /** Called when skin selection changes */
  static saveSkinSelection(): void { Profile.save(); }

  // ==================== Persistence ====================

  private static save(): void {
    try {
      const out = new DataOutputStream(Profile.FILE);
      out.writeInt(Profile.VERSION);

      // XP & Level
      out.writeLong(Profile.totalXp);
      out.writeInt(Profile.level);

      // Per-difficulty
      for (let i = 0; i < 3; i++) out.writeInt(Profile.attempts[i]);
      for (let i = 0; i < 3; i++) out.writeInt(Profile.highScores[i]);
      for (let i = 0; i < 3; i++) out.writeInt(Profile.highLevels[i]);
      for (let i = 0; i < 3; i++) out.writeInt(Profile.bestTimeTicks[i]);

      // Lifetime
      out.writeInt(Profile.totalGames);
      out.writeInt(Profile.totalDeaths);
      out.writeLong(Profile.totalTimeTicks);
      out.writeLong(Profile.totalScore);
      out.writeLong(Profile.totalDamageTaken);
      out.writeInt(Profile.totalUpgradesBought);
      out.writeInt(Profile.totalHealthUps);
      out.writeInt(Profile.totalSpeedUps);
      out.writeInt(Profile.totalRefills);
      out.writeInt(Profile.longestStreakTicks);
      out.writeInt(Profile.totalBossesDefeated);

      // Enemies
      out.writeInt(Profile.enemyBasic);
      out.writeInt(Profile.enemyFast);
      out.writeInt(Profile.enemySmart);
      out.writeInt(Profile.enemyHard);
      out.writeInt(Profile.enemyBoss);

      // History
      out.writeInt(Profile.historyIndex);
      out.writeInt(Profile.historyCount);
      for (let i = 0; i < Profile.HISTORY_SIZE; i++) out.writeInt(Profile.recentScores[i]);
      for (let i = 0; i < Profile.HISTORY_SIZE; i++) out.writeInt(Profile.recentLevels[i]);
      for (let i = 0; i < Profile.HISTORY_SIZE; i++) out.writeInt(Profile.recentDifficulty[i]);

      // v2: Skin selection
      out.writeInt(PlayerSkins.getShapeId());
      out.writeInt(PlayerSkins.getColorId());

      // v3: Coins + CoinShop purchases
      out.writeLong(Profile.coins);
      out.writeInt(CoinShop.ITEM_COUNT);
      for (let i = 0; i < CoinShop.ITEM_COUNT; i++) {
        out.writeBoolean(CoinShop.isPurchased(i));
      }

      out.close();
    } catch {
      // Silent fail
    }
  }

  private static load(): void {
    // Try to import legacy Stats.dat if Profile doesn't exist yet
    const in_ = FileSystem.open(Profile.FILE);
    if (in_ === null) {
      Profile.importLegacyStats();
      return;
    }
    try {
      const version = in_.readInt();
      if (version >= 1) {
        Profile.totalXp = Number(in_.readLong());
        Profile.level = in_.readInt();

        for (let i = 0; i < 3; i++) Profile.attempts[i] = in_.readInt();
        for (let i = 0; i < 3; i++) Profile.highScores[i] = in_.readInt();
        for (let i = 0; i < 3; i++) Profile.highLevels[i] = in_.readInt();
        for (let i = 0; i < 3; i++) Profile.bestTimeTicks[i] = in_.readInt();

        Profile.totalGames = in_.readInt();
        Profile.totalDeaths = in_.readInt();
        Profile.totalTimeTicks = Number(in_.readLong());
        Profile.totalScore = Number(in_.readLong());
        Profile.totalDamageTaken = Number(in_.readLong());
        Profile.totalUpgradesBought = in_.readInt();
        Profile.totalHealthUps = in_.readInt();
        Profile.totalSpeedUps = in_.readInt();
        Profile.totalRefills = in_.readInt();
        Profile.longestStreakTicks = in_.readInt();
        Profile.totalBossesDefeated = in_.readInt();

        Profile.enemyBasic = in_.readInt();
        Profile.enemyFast = in_.readInt();
        Profile.enemySmart = in_.readInt();
        Profile.enemyHard = in_.readInt();
        Profile.enemyBoss = in_.readInt();

        Profile.historyIndex = in_.readInt();
        Profile.historyCount = in_.readInt();
        for (let i = 0; i < Profile.HISTORY_SIZE; i++) Profile.recentScores[i] = in_.readInt();
        for (let i = 0; i < Profile.HISTORY_SIZE; i++) Profile.recentLevels[i] = in_.readInt();
        for (let i = 0; i < Profile.HISTORY_SIZE; i++) Profile.recentDifficulty[i] = in_.readInt();

        // v2: Skin selection
        if (version >= 2) {
          PlayerSkins.loadSelection(in_.readInt(), in_.readInt());
        }

        // v3: Coins + CoinShop purchases
        if (version >= 3) {
          Profile.coins = Number(in_.readLong());
          const itemCount = in_.readInt();
          const purchased: boolean[] = new Array<boolean>(itemCount).fill(false);
          for (let i = 0; i < itemCount; i++) purchased[i] = in_.readBoolean();
          CoinShop.loadPurchased(purchased);
        }
      }
    } catch {
      // Corrupted or old format — defaults are fine
    }
  }

  /** Import data from legacy dotch_stats.dat if it exists */
  private static importLegacyStats(): void {
    const in_ = FileSystem.open('dotch_stats.dat');
    if (in_ === null) return;
    try {
      for (let i = 0; i < 3; i++) Profile.attempts[i] = in_.readInt();
      for (let i = 0; i < 3; i++) Profile.highScores[i] = in_.readInt();
      Profile.totalGames = Profile.attempts[0] + Profile.attempts[1] + Profile.attempts[2];
      Profile.totalDeaths = Profile.totalGames; // reasonable estimate
      Profile.save();
    } catch {
      // Can't read legacy — start fresh
    }
  }

  private static formatTicks(ticks: number): string {
    const totalSec = Math.trunc(ticks / 60);
    const min = Math.trunc(totalSec / 60);
    const sec = totalSec % 60;
    if (min > 0) return min + 'm ' + sec + 's';
    return sec + 's';
  }
}
